import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { favouritesService } from '../../services/supabaseService';
import { Heart, MessageCircle } from 'lucide-react';

const DashboardFavouritesPage = () => {
  const { user, userProfile } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    favouritesService.list(user.id).then((data) => {
      setList(Array.isArray(data) ? data : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user?.id]);

  const remove = async (providerId) => {
    if (!user?.id) return;
    try {
      await favouritesService.remove(user.id, providerId);
      setList((prev) => prev.filter((p) => (p.provider_id || p.user_profiles?.id) !== providerId));
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  const canMessage = ['premium', 'active', 'trialing'].includes(
    String(userProfile?.subscriptionStatus ?? userProfile?.subscription_status ?? 'free').toLowerCase()
  ) || userProfile?.role === 'service_provider';

  return (
    <div>
      <h1 className="font-heading font-bold text-2xl text-white mb-6">Favourites</h1>
      {list.length === 0 ? (
        <div className="p-12 rounded-2xl bg-slate-800/50 border border-white/10 text-center text-slate-400">
          <Heart className="h-12 w-12 mx-auto mb-4 text-slate-500" />
          <p>No saved providers yet. Save friends from Find a Friend.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((fav) => {
            const p = fav.user_profiles || fav;
            const id = p.id || fav.provider_id;
            return (
              <div
                key={fav.id || id}
                className="rounded-2xl overflow-hidden bg-slate-800/50 border border-white/10 flex"
              >
                <div className="w-24 h-24 shrink-0 bg-slate-700 overflow-hidden">
                  <img
                    src={p.avatar_url || '/default-avatar.svg'}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-4 flex-1 min-w-0">
                  <h3 className="font-semibold text-white truncate">
                    {p.display_name || p.username || 'Friend'}
                  </h3>
                  {p.city && <p className="text-sm text-slate-400">{p.city}</p>}
                  <div className="mt-2 flex gap-2">
                    {canMessage ? (
                      <Link
                        to={`/dashboard/chats?with=${id}`}
                        className="flex items-center gap-1 py-1.5 px-3 rounded-lg bg-brand-pink text-white text-sm font-medium hover:bg-brand-pink-dark"
                      >
                        <MessageCircle className="h-3.5 w-3.5" />
                        Message
                      </Link>
                    ) : (
                      <Link
                        to="/payment"
                        className="flex items-center gap-1 py-1.5 px-3 rounded-lg bg-amber-500 text-white text-sm font-medium"
                      >
                        Upgrade to message
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => remove(id)}
                      className="py-1.5 px-3 rounded-lg border border-white/20 text-slate-400 text-sm hover:bg-red-500/20 hover:text-red-400"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DashboardFavouritesPage;
